import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    Container,
    PageHeader,
    TitleHead,
    PageWrap,
    Button,
    FilterContainer,
    Filter,
    FilterTitle,
    FilterItem,
} from "static/coreStyles/global.styled";
import TableHead from "components/TableHead";
import { tableHead } from "./data";
import {
    TableWrap,
    TableHeadWrap,
    TableContentWrap,
} from "static/coreStyles/table.styled";
import { GroupList } from "./GroupList/GroupList";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import EmptyState from "components/emptyState";
import Pagination from "components/pagination";
import { getGroupList, setFilters, updateFilters } from "actions/group";
import { useDebounce } from "utility/helperFunctions";
import { DatePicker, Modal, Slider, Switch, Typography } from "antd";
import { CrossTabFilterTag } from "components/CrossTabFilterTag";
import { useSetState } from "utility/helperFunctions";
import { Theme } from "static/coreStyles/theme.styled";
import Search from "components/Search";
import dayjs from "utils/dateUtils";
import { omit } from "lodash";
import { HeaderButtons } from "components/Header";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const DEFAULT_FILTER_MODAL_STATE = {
    dateRange: null,
    distanceRange: null,
    memberRange: null,
    rideCountRange: null,
    isVerified: null,
    isLiveTrackingEnabledSingleDay: null,
    isLiveTrackingEnabledMultiDay: null,
};

const filterChipRowStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "16px",
};

const GroupFilterContainer = styled(FilterContainer)`
    gap: 28px;
`;

const GroupFilter = styled(Filter)`
    align-items: center;
    margin: 0;
    min-height: 32px;
`;

function Groups({
    groupList,
    fetchGroupList,
    updateGroupFilter,
    setGroupFilter,
    filters,
    pagination,
}) {
    const sortMap = filters.sort || {};
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, 800);
    const [modalVisible, setModalVisible] = useState(false);
    const isDrillDownFilter = Boolean(filters.userId);

    useEffect(() => {
        if (isDrillDownFilter) return;
        if (debouncedSearchTerm) {
            updateGroupFilter({ query: debouncedSearchTerm });
        } else {
            updateGroupFilter({ query: "" });
        }
    }, [debouncedSearchTerm, isDrillDownFilter]);

    useEffect(() => {
        if (filters.userId) {
            setSearchTerm("");
            if (filters.query) {
                updateGroupFilter({ query: "" });
            }
        }
    }, [filters.userId]);

    const handleSort = (key, value) => {
        if (value === "") {
            const sortKeys = omit(filters.sort, key);
            updateGroupFilter({ sort: sortKeys });
        } else {
            updateGroupFilter({
                sort: {
                    ...(filters.sort || {}),
                    [key]: value,
                },
            });
        }
    };

    useEffect(() => {
        fetchGroupList({ ...filters, pageSize: 10 });
    }, [filters]);

    const handleRemoveUserFilter = () => {
        setGroupFilter({
            page: 0,
            sort: { createdAt: 'desc' },
        });
    };

    const handleRemoveVerifiedFilter = () => {
        const { isVerified, ...rest } = filters;
        setGroupFilter({ ...rest, page: 0 });
    };

    const handleRemoveSingleDayFilter = () => {
        const { isLiveTrackingEnabledSingleDay, ...rest } = filters;
        setGroupFilter({ ...rest, page: 0 });
    };

    const handleRemoveMultiDayFilter = () => {
        const { isLiveTrackingEnabledMultiDay, ...rest } = filters;
        setGroupFilter({ ...rest, page: 0 });
    };

    const hasFilterChips =
        filters.userId ||
        filters.isVerified ||
        filters.isLiveTrackingEnabledSingleDay ||
        filters.isLiveTrackingEnabledMultiDay;

    return (
        <PageWrap>
            <Container>
                <PageHeader>
                    <TitleHead>
                        <Search
                            setSearchTerm={setSearchTerm}
                            onFilterClick={() => setModalVisible(true)}
                            filters={filters}
                            setFilter={setGroupFilter}
                            filterDisabled={isDrillDownFilter}
                            searchDisabled={isDrillDownFilter}
                        />
                        <HeaderButtons />
                    </TitleHead>
                </PageHeader>

                {hasFilterChips && (
                    <div style={filterChipRowStyle}>
                        {filters.userId && (
                            <CrossTabFilterTag
                                label="User"
                                value={filters.userName?.trim() || 'NA'}
                                onClose={handleRemoveUserFilter}
                            />
                        )}
                        {filters.isVerified && (
                            <CrossTabFilterTag
                                label="Verified Groups"
                                value="ON"
                                onClose={handleRemoveVerifiedFilter}
                            />
                        )}
                        {filters.isLiveTrackingEnabledSingleDay && (
                            <CrossTabFilterTag
                                label="Live Tracking (Single-Day)"
                                value="ON"
                                onClose={handleRemoveSingleDayFilter}
                            />
                        )}
                        {filters.isLiveTrackingEnabledMultiDay && (
                            <CrossTabFilterTag
                                label="Live Tracking (Multi-Day)"
                                value="ON"
                                onClose={handleRemoveMultiDayFilter}
                            />
                        )}
                    </div>
                )}

                {groupList && groupList.length > 0 ? (
                    <TableWrap>
                        <TableHeadWrap className="group-table-head">
                            {tableHead.map((item, index) => {
                                const sortingKey =
                                    item.sortKey &&
                                        sortMap &&
                                        sortMap.hasOwnProperty(item.sortKey)
                                        ? sortMap[item.sortKey]
                                        : null;
                                return (
                                    <TableHead
                                        data={item}
                                        key={index}
                                        isSorted={!!item.sortKey && !isDrillDownFilter}
                                        handleSort={() => {
                                            const isBoolean = item.sortKey && item.sortKey.startsWith("is");

                                            if (isBoolean) {
                                                if (sortingKey === true) {
                                                    handleSort(item.sortKey, false);
                                                } else if (sortingKey === false) {
                                                    handleSort(item.sortKey, null);
                                                } else {
                                                    handleSort(item.sortKey, true);
                                                }
                                            } else {
                                                if (sortingKey === "desc") {
                                                    handleSort(item.sortKey, "asc");
                                                } else if (sortingKey === "asc") {
                                                    handleSort(item.sortKey, null);
                                                } else {
                                                    handleSort(item.sortKey, "desc");
                                                }
                                            }
                                        }}

                                        sort={sortingKey}
                                    />
                                );
                            })}
                        </TableHeadWrap>
                        <TableContentWrap>
                            {groupList.map((item, index) => (
                                <GroupList data={item} key={index} />
                            ))}
                        </TableContentWrap>
                    </TableWrap>
                ) : filters.userId ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Text type="secondary">
                            {`No groups found for user "${filters.userName?.trim() || 'NA'}"`}
                        </Text>
                    </div>
                ) : (
                    <EmptyState />
                )}
                <Pagination
                    page={filters.page}
                    pageSize={10}
                    pagination={pagination}
                    updatePage={(newPage) => updateGroupFilter({ page: newPage })}
                />
                <FilterModal
                    filters={filters}
                    open={modalVisible}
                    setVisible={setModalVisible}
                    setFilter={setGroupFilter}
                    updateFilter={updateGroupFilter}
                />
            </Container>
        </PageWrap>
    );
}

function FilterModal({ open, setVisible, setFilter, filters }) {
    const [state, setState] = useSetState(DEFAULT_FILTER_MODAL_STATE);

    useEffect(() => {
        if (!filters || Object.keys(filters).length === 0) {
            setState(DEFAULT_FILTER_MODAL_STATE);
        }
    }, [filters]);

    useEffect(() => {
        if (open) {
            setState({
                isVerified: filters.isVerified || null,
                isLiveTrackingEnabledSingleDay: filters.isLiveTrackingEnabledSingleDay || null,
                isLiveTrackingEnabledMultiDay: filters.isLiveTrackingEnabledMultiDay || null,
            });
        }
    }, [open, filters.isVerified, filters.isLiveTrackingEnabledSingleDay, filters.isLiveTrackingEnabledMultiDay]);

    const resetModalState = () => setState(DEFAULT_FILTER_MODAL_STATE);

    return (
        <Modal
            title="Apply Filter"
            open={open}
            styles={{
                body: {
                    padding: "24px",
                    borderTop: "1px solid #f0f0f0",
                    borderBottom: "1px solid #f0f0f0",
                },
                footer: {
                    padding: "8px 0 0 0",   
                    marginTop: 0,
                },
            }}
            onCancel={() => {
                resetModalState();
                setVisible(false);
            }}
            footer={[
                <Button
                    key="back"
                    onClick={() => {
                        setFilter(null);
                        resetModalState();
                        setVisible(false);
                    }}
                >
                    Reset
                </Button>,
                <Button
                    key="submit"
                    primary
                    color={Theme.blueColor}
                    onClick={() => {
                        const {
                            dateRange,
                            distanceRange,
                            memberRange,
                            rideCountRange,
                            isVerified,
                            isLiveTrackingEnabledSingleDay,
                            isLiveTrackingEnabledMultiDay,
                        } = state;
                        const filterData = {};
                        if (dateRange) {
                            if (dateRange[0]) {
                                filterData["creationDateAfter"] = dayjs(dateRange[0])
                                    .format("YYYY-MM-DD")
                                    .toString();
                            }
                            if (dateRange[1]) {
                                filterData["creationDateBefore"] = dayjs(dateRange[1])
                                    .format("YYYY-MM-DD")
                                    .toString();
                            }
                        }
                        if (distanceRange) {
                            filterData["distanceRange"] = `${distanceRange[0]}-${distanceRange[1]}`;
                        }
                        if (memberRange) {
                            filterData["memberRange"] = `${memberRange[0]}-${memberRange[1]}`;
                        }
                        if (rideCountRange) {
                            filterData["rideCountRange"] = `${rideCountRange[0]}-${rideCountRange[1]}`;
                        }
                        if (isVerified) {
                            filterData["isVerified"] = isVerified;
                        }
                        if (isLiveTrackingEnabledSingleDay) {
                            filterData["isLiveTrackingEnabledSingleDay"] = true;
                        }
                        if (isLiveTrackingEnabledMultiDay) {
                            filterData["isLiveTrackingEnabledMultiDay"] = true;
                        }
                        setFilter({
                            page: 0,
                            sort: { createdAt: "desc" },
                            ...filterData,
                        });
                        setVisible(false);
                    }}
                >
                    Apply
                </Button>,
            ]}
        >
            <GroupFilterContainer>
                <GroupFilter>
                    <FilterTitle>Creation Date Range</FilterTitle>
                    <FilterItem>
                        <RangePicker
                            allowEmpty={[true, true]}
                            value={state.dateRange}
                            onChange={(value) => setState({ dateRange: value })}
                        />
                    </FilterItem>
                </GroupFilter>
                <GroupFilter>
                    <FilterTitle>Distance Range</FilterTitle>
                    <FilterItem>
                        <Slider
                            style={{ flex: 1 }}
                            step={1}
                            min={0}
                            max={100}
                            tooltipPlacement="bottomLeft"
                            range
                            value={state.distanceRange}
                            onChange={(value) => setState({ distanceRange: value })}
                        />
                    </FilterItem>
                </GroupFilter>
                <GroupFilter>
                    <FilterTitle>Members Range</FilterTitle>
                    <FilterItem>
                        <Slider
                            style={{ flex: 1 }}
                            step={1}
                            min={0}
                            max={20}
                            tooltipPlacement="bottomLeft"
                            range
                            value={state.memberRange}
                            onChange={(value) => setState({ memberRange: value })}
                        />
                    </FilterItem>
                </GroupFilter>
                <GroupFilter>
                    <FilterTitle>Ride Count Range</FilterTitle>
                    <FilterItem>
                        <Slider
                            style={{ flex: 1 }}
                            step={1}
                            min={0}
                            max={20}
                            tooltipPlacement="bottomLeft"
                            range
                            value={state.rideCountRange}
                            onChange={(value) => setState({ rideCountRange: value })}
                        />
                    </FilterItem>
                </GroupFilter>
                <GroupFilter>
                    <FilterTitle>Verified Groups</FilterTitle>
                    <FilterItem>
                        <Switch
                            checked={state.isVerified}
                            onChange={(checked) => setState({ isVerified: checked })}
                        />
                    </FilterItem>
                </GroupFilter>
                <GroupFilter>
                    <FilterTitle>Live Tracking (Single-Day Rides)</FilterTitle>
                    <FilterItem>
                        <Switch
                            checked={state.isLiveTrackingEnabledSingleDay}
                            onChange={(checked) =>
                                setState({ isLiveTrackingEnabledSingleDay: checked })
                            }
                        />
                    </FilterItem>
                </GroupFilter>
                <GroupFilter>
                    <FilterTitle>Live Tracking (Multi-Day Rides)</FilterTitle>
                    <FilterItem>
                        <Switch
                            checked={state.isLiveTrackingEnabledMultiDay}
                            onChange={(checked) =>
                                setState({ isLiveTrackingEnabledMultiDay: checked })
                            }
                        />
                    </FilterItem>
                </GroupFilter>
            </GroupFilterContainer>
        </Modal>
    );
}

function mapStateToProps(state) {
    const {
        Group: { groupList, filters, pagination },
    } = state;
    return { groupList, filters, pagination };
}

function mapDispatchToProps(dispatch) {
    return {
        fetchGroupList: bindActionCreators(getGroupList, dispatch),
        setGroupFilter: bindActionCreators(setFilters, dispatch),
        updateGroupFilter: bindActionCreators(updateFilters, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(Groups);
